import {
  AlphabeticalAsc, AlphabeticalDesc,
  FilterContentFieldDataTypes,
  IFilter,
  Newest, Oldest,
  SortDirection, SortDropdownOption
} from '@hevoio/hevo-ui-toolkit';
import { edgeRoute, standardRoute } from '../../constants/pipelineRoutes';
import { UNIFIED_STATUS_DATA_MAP } from '../../constants/unifiedPipelineStatus';
import { UnifiedDestinationsAPI } from '../../../destination/api/UnifiedDestinationsAPI';
import {
  UnifiedPipeline,
  UnifiedPipelineEngine,
  UnifiedPipelineStatus
} from './interfaces/unifiedPipeline';

// Re-exported for list-internal consumers that already import it from here; the
// canonical definition lives in the shared pipeline status vocabulary.
export { UNIFIED_STATUS_DATA_MAP };

// Routes are the SAME ones the existing per-engine lists use — no new routes.
// Every row routes to its engine's detail page (edge by native id, standard by
// seq id). Draft rows never reach the listing (the endpoint excludes them), so
// there is no draft/resume-create branch here.
export function getUnifiedPipelineDetailPath(row: UnifiedPipeline): string {
  if (row.engine === UnifiedPipelineEngine.EDGE) {
    return edgeRoute.overview(row.id);
  }

  return standardRoute.overview(row.seq_id);
}

// Status filter options. The option `value` IS the unified status key, so the
// in-memory list matches it directly against the mapped `item.status` — no
// raw-engine expansion. Disabling/Enabling/Deleting/Deleted and Draft are
// chip-only (rendered on rows, not offered as filter options).
const UNIFIED_STATUS_FILTER_OPTIONS: { label: string; value: UnifiedPipelineStatus }[] = [
  { label: 'Active', value: UnifiedPipelineStatus.ACTIVE },
  { label: 'Disabled', value: UnifiedPipelineStatus.DISABLED },
  { label: 'Failed', value: UnifiedPipelineStatus.FAILED },
  { label: 'In Progress', value: UnifiedPipelineStatus.IN_PROGRESS }
];

export const UNIFIED_PIPELINE_FILTERS: IFilter[] = [
  {
    title: 'Destination',
    filterKey: 'destination_name',
    filterType: FilterContentFieldDataTypes.LIST,
    filterContentProps: {
      multiple: true,
      // One combined call — the middleware aggregates both engines'
      // destination APIs, mirroring the pipelines listing endpoint.
      dataSource: () =>
        UnifiedDestinationsAPI.getDestinations()
          .then(res => {
            const seen = new Set<string>();

            return (res.data || [])
              .map(d => ({
                label: d.name,
                value: d.name,
                logo: { logo: d.logo_url, logoDarkMode: d.dark_mode_logo_url }
              }))
              .filter(option => {
                if (seen.has(option.value)) return false;
                seen.add(option.value);
                return true;
              });
          })
          // A failed destinations fetch must not break the filter UI: degrade to
          // an empty option list (the rest of the toolbar keeps working) instead
          // of leaving the dropdown stuck on an unhandled rejection.
          .catch(() => []),
      textFieldProps: {
        prefixIcon: 'search',
        placeholder: 'Search Destination'
      }
    }
  },
  {
    title: 'Status',
    filterKey: 'status',
    filterType: FilterContentFieldDataTypes.LIST,
    filterContentProps: {
      multiple: true,
      selectMode: true,
      options: UNIFIED_STATUS_FILTER_OPTIONS
    }
  }
];

export const UNIFIED_PIPELINE_SORT_DROPDOWN_OPTIONS: SortDropdownOption[] = [
  { label: Newest, key: 'created_ts', direction: SortDirection.DESC },
  { label: Oldest, key: 'created_ts', direction: SortDirection.ASC },
  { label: AlphabeticalAsc, key: 'name', direction: SortDirection.ASC },
  { label: AlphabeticalDesc, key: 'name', direction: SortDirection.DESC }
];
